import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { DtcAPI } from "../lib/api";
import { extractError } from "../lib/api";
import { DtcCodeChip } from "./DtcCodeChip";

/**
 * Phase 7 — DTC ingest dialog.
 *
 * Chip input + autocomplete against ``/dtc/catalogue?q=``. Optional
 * freeze-frame text (we accept any string and store it as a single-key
 * blob — real OBD tools paste their own structure).
 */
export function DtcIngestDialog({ jobId, onIngested, triggerLabel = "Add DTC" }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [codes, setCodes] = useState([]);            // [{code, severity, description}]
    const [freezeFrame, setFreezeFrame] = useState("");
    const [mileage, setMileage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!query.trim()) { setSuggestions([]); return; }
        const t = setTimeout(() => {
            DtcAPI.catalogue({ q: query.trim().toUpperCase(), page_size: 8 }).then((d) => {
                setSuggestions(d.items || []);
            }).catch(() => setSuggestions([]));
        }, 200);
        return () => clearTimeout(t);
    }, [query]);

    const addCode = (item) => {
        if (codes.find((c) => c.code === item.code)) return;
        setCodes([...codes, item]);
        setQuery("");
        setSuggestions([]);
    };

    const addRawCode = () => {
        const c = query.trim().toUpperCase();
        if (!/^[PCBU][0-9A-F]{4}$/.test(c)) {
            toast.error("Invalid DTC format. Expected P/C/B/U + 4 hex digits.");
            return;
        }
        DtcAPI.decode(c).then((dto) => {
            addCode({ code: c, severity: dto.severity, description: dto.description });
        }).catch(() => {
            addCode({ code: c, severity: "unknown", description: "Unknown DTC" });
        });
    };

    const removeCode = (code) => setCodes(codes.filter((c) => c.code !== code));

    const submit = async () => {
        if (!codes.length) {
            toast.error("Add at least one DTC code");
            return;
        }
        setSubmitting(true);
        try {
            let parsedFF = null;
            if (freezeFrame.trim()) {
                try { parsedFF = JSON.parse(freezeFrame); }
                catch { parsedFF = { raw: freezeFrame.trim() }; }
            }
            const payload = {
                codes: codes.map((c) => ({
                    code: c.code,
                    freeze_frame: parsedFF,
                    mileage_at_read: mileage ? Number(mileage) : null,
                })),
                source: "manual",
            };
            const r = await DtcAPI.ingestForJob(jobId, payload);
            toast.success(`Added ${r.count} DTC(s)`);
            setOpen(false);
            setCodes([]); setQuery(""); setFreezeFrame(""); setMileage("");
            onIngested?.(r);
        } catch (e) {
            toast.error(extractError(e));
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="dtc-add-btn" variant="default">{triggerLabel}</Button>
            </DialogTrigger>
            <DialogContent data-testid="dtc-ingest-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Add diagnostic trouble code(s)</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider opacity-70">Code</label>
                        <div className="flex gap-2">
                            <Input
                                data-testid="dtc-ingest-code-input"
                                value={query}
                                onChange={(e) => setQuery(e.target.value.toUpperCase())}
                                placeholder="e.g. P0301"
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRawCode(); } }}
                            />
                            <Button variant="outline" onClick={addRawCode} data-testid="dtc-ingest-add-raw">Add</Button>
                        </div>
                        {suggestions.length > 0 && (
                            <ul data-testid="dtc-ingest-suggestions" className="border rounded-md divide-y max-h-40 overflow-auto bg-background">
                                {suggestions.map((s) => (
                                    <li key={s.code}>
                                        <button
                                            type="button"
                                            data-testid={`dtc-ingest-suggestion-${s.code}`}
                                            onClick={() => addCode(s)}
                                            className="w-full text-left px-3 py-1.5 hover:bg-muted text-sm flex items-center justify-between gap-3"
                                        >
                                            <span className="font-mono">{s.code}</span>
                                            <span className="text-xs opacity-70 truncate">{s.description}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {codes.length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="dtc-ingest-selected">
                            {codes.map((c) => (
                                <span key={c.code} className="inline-flex items-center gap-1.5">
                                    <DtcCodeChip code={c.code} severity={c.severity}
                                                      description={c.description}
                                                      data-testid={`dtc-ingest-pill-${c.code}`} />
                                    <button type="button" onClick={() => removeCode(c.code)}
                                              data-testid={`dtc-ingest-remove-${c.code}`}
                                              className="text-xs opacity-60 hover:opacity-100">×</button>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider opacity-70">Mileage at read (optional)</label>
                        <Input data-testid="dtc-ingest-mileage" type="number" min="0"
                                  value={mileage} onChange={(e) => setMileage(e.target.value)}
                                  placeholder="e.g. 41250" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider opacity-70">Freeze frame (optional, paste JSON or notes)</label>
                        <Textarea data-testid="dtc-ingest-freezeframe" rows={3}
                                       value={freezeFrame} onChange={(e) => setFreezeFrame(e.target.value)}
                                       placeholder='{"rpm": 2400, "coolant_temp_c": 92}' />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="dtc-ingest-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={submitting || !codes.length}
                              data-testid="dtc-ingest-submit">
                        {submitting ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
